import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaArrowRight } from 'react-icons/fa';
import { Container, Button, theme } from '../../styles/GlobalStyles';

const HeroContainer = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: ${theme.gradients.hero};
  position: relative;
  overflow: hidden;
  padding-top: 80px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%2306b6d4" fill-opacity="0.1"><circle cx="30" cy="30" r="2"/></g></g></svg>');
    animation: float 20s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(6, 182, 212, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.1) 0%, transparent 50%);
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  color: white;
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2.8rem;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  margin-bottom: 40px;
  line-height: 1.6;
  opacity: 0.9;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const StyledButton = styled(Button)`
  padding: 15px 30px;
  font-size: 1.1rem;
  border-radius: 50px;
  min-width: 180px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    min-width: 200px;
  }
`;

const FloatingIcon = styled(motion.div)`
  position: absolute;
  font-size: 2rem;
  opacity: 0.1;
  color: white;
`;

const HeroSection: React.FC = () => {
  const floatingIcons = [
    { top: '20%', left: '10%', delay: 0 },
    { top: '30%', right: '15%', delay: 0.5 },
    { top: '60%', left: '5%', delay: 1 },
    { top: '70%', right: '10%', delay: 1.5 },
    { top: '40%', left: '85%', delay: 2 },
  ];

  return (
    <HeroContainer>
      <Container>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Unleashing the Full Potential of{' '}
            <span style={{ color: theme.colors.accent }}>
              Governance, Risk, and Compliance
            </span>{' '}
            through Expert Cybersecurity Consulting Services
          </HeroTitle>
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Comprehensive cybersecurity solutions tailored for modern enterprises. 
            From compliance management to advanced threat protection, we secure your digital transformation journey.
          </HeroSubtitle>
          
          <HeroButtons
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <StyledButton variant="primary">
              Get Started <FaArrowRight />
            </StyledButton>
            <StyledButton variant="outline">
              Our Services
            </StyledButton>
          </HeroButtons>
        </HeroContent>
      </Container>

      {floatingIcons.map((icon, index) => (
        <FloatingIcon
          key={index}
          style={{
            top: icon.top,
            left: icon.left,
            right: icon.right
          }}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 4,
            delay: icon.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <FaShieldAlt />
        </FloatingIcon>
      ))}
    </HeroContainer>
  );
};

export default HeroSection;

